<template>
  <section class="mission-panel" :class="{ embedded }">
    <div v-if="!embedded" class="panel-heading gs-section-heading has-icon"><span><i class="iconfont icon-songcanfuwu" aria-hidden="true"></i>{{ $t("当前任务") }}</span><GroundStationStatusBadge>{{ $t("{0} 单待分配", [(availableOrders.length)]) }}</GroundStationStatusBadge></div>
    <slot v-if="compact" name="compact" :title="$t(missionTitle)" :state="compactState" :info="compactInfo" :step="compactStep" :animate-step="animateStep" :result="completionResult" :progress="missionProgress" :delivery-progress="missionDeliveryProgress" :ready="Boolean(plane)" :progress-active="Boolean(executeOrder && plane && plane.planeState && plane.planeState.online) && currentQuestState === 8" :has-mission="Boolean(executeOrder || selectedOrder)" :action-label="$t(primaryLabel)" :action-icon="primaryIcon" :action-disabled="primaryDisabled" :action-visible="primaryVisible" :run-action="runPrimary" />
    <transition name="workbench-detail">
    <div v-show="!compact || expanded" class="mission-card" :class="{ 'compact-details': compact }">
      <header v-if="compact" class="compact-details-header"><h2>{{ $t("任务详情") }}</h2><UiIconControl variant="ghost" :title="$t('关闭任务详情')" icon="el-icon-close" @click="$emit('close-details')" /></header>
      <div :class="{ 'compact-details-body': compact }">
      <UiInfoTile class="mission-summary-tile" :emphasis="false">
        <template #value>
          <div class="mission-title">
            <strong>{{ $t(missionTitle) }}</strong>
            <UiTag :tone="executeOrder ? 'warning' : 'neutral'" size="mini">{{ $t(missionState) }}</UiTag>
          </div>

          <template v-if="plane">
            <div
              v-if="!executeOrder"
              class="mission-order-select-shell"
              :class="{ 'has-selected-risks': selectedOrderRisks.length }"
            >
              <h3 v-if="!compact" class="mission-order-step">{{ $t("选择待配送订单") }}</h3>
              <UiSelect
                ref="orderSelect"
                v-model="selectedOrderId"
                class="mission-order-select"
                :placeholder="$t('选择待配送订单')"
                popper-class="ground-station-shop-menu ground-station-mission-menu"
              >
                <el-option
                  v-for="option in availableOrderOptions"
                  :key="option.order.id"
                  :label="$t(orderLabel(option.order))"
                  :value="option.order.id"
                  :class="{ 'is-risky': option.risks.length }"
                >
                  <div class="mission-order-option">
                    <span v-if="option.risks.length" class="mission-order-option__alert">!</span>
                    <span class="mission-order-option__label">{{ $t(orderLabel(option.order)) }}</span>
                    <span v-if="option.risks.length" class="mission-order-option__risks">
                      <UiTag
                        v-for="risk in option.risks"
                        :key="risk.key"
                        :tone="risk.tone"
                        :title="$t(risk.label)"
                        size="mini"
                      >{{ $t(risk.shortLabel) }}</UiTag>
                    </span>
                  </div>
                </el-option>
              </UiSelect>
              <h3 v-if="!compact && selectedOrderRisks.length" class="mission-section-label">{{ $t("提示") }}</h3>
              <div v-if="!compact && selectedOrderRisks.length" class="mission-selected-risks" role="status">
                <UiTag
                  v-for="risk in selectedOrderRisks"
                  :key="risk.key"
                  :tone="risk.tone"
                  :title="$t(risk.label)"
                  size="mini"
                ><span class="ui-tag__text">{{ $t(risk.label) }}</span></UiTag>
              </div>
            </div>

            <dl v-else>
              <div><dt>{{ $t("收货站点") }}</dt><dd>{{ executeOrder.receive_site_name || $t('未命名站点') }}</dd></div>
              <div><dt>{{ $t("叫餐号") }}</dt><dd>{{ executeOrder.food_sn || '--' }}</dd></div>
            </dl>

            <h3 v-if="!compact && missionRisks.length" class="mission-section-label mission-warning-label">{{ $t("提示") }}</h3>
            <div v-if="!compact && missionRisks.length" class="mission-warnings" role="status" aria-live="polite">
              <UiTag
                v-for="risk in missionRisks"
                :key="risk.key"
                :tone="risk.tone"
                :title="$t(risk.label)"
              ><span class="ui-tag__text">{{ $t(risk.label) }}</span></UiTag>
            </div>
          </template>

          <div v-else class="mission-empty">{{ $t("请先选择飞机") }}</div>
        </template>
      </UiInfoTile>

      <div v-if="plane" :class="{ 'mission-footer': compact }">
        <h3 v-if="!compact" class="mission-section-label mission-actions-label">{{ $t("订单操作") }}</h3>
        <div v-if="!compact || !plane.planeState?.online || missionActionBlocked || currentQuestState !== 8" class="mission-actions"><UiButton tone="warning" :loading="primaryLoading" :disabled="primaryDisabled" @click="runPrimary">{{ $t(primaryLabel) }}</UiButton></div>

        <div v-if="showCargoConfirm" class="cargo-actions">
          <UiButton tone="danger" icon="el-icon-close" :disabled="finishingMission || executionPending" @click="finishMission('undelivered')">{{ $t("未送达") }}</UiButton>
          <UiButton tone="accent" icon="el-icon-check" :disabled="finishingMission || executionPending" @click="finishMission('delivered')">{{ $t("已送达") }}</UiButton>
        </div>
      </div>
      <template v-if="compact && plane">
        <h3 v-if="selectedOrderRisks.length || missionRisks.length" class="mission-section-label mission-warning-label">{{ $t("提示") }}</h3>
        <div v-if="selectedOrderRisks.length" class="mission-selected-risks" role="status">
          <UiTag v-for="risk in selectedOrderRisks" :key="risk.key" :tone="risk.tone" :title="$t(risk.label)" size="mini"><span class="ui-tag__text">{{ $t(risk.label) }}</span></UiTag>
        </div>
        <div v-if="missionRisks.length" class="mission-warnings" role="status" aria-live="polite">
          <UiTag v-for="risk in missionRisks" :key="risk.key" :tone="risk.tone" :title="$t(risk.label)"><span class="ui-tag__text">{{ $t(risk.label) }}</span></UiTag>
        </div>
      </template>
      </div>
    </div>
    </transition>
  </section>
</template>

<script>
import { t } from '@/i18n'
import { startDeliveryTask, finishDeliveryTask, getDeliveryTask, dispatchDeliveryCommand } from '@/utils/api/table'
import mqtt from '@/utils/mqtt'
import { executeConfirmedMission, requestMissionReply, resetOrphanMissionManually } from '@/utils/missionSafety'
import { speakText } from '@/utils/index'
import { normalizeSiteRuntime } from '@/utils/siteRuntime'
import { hasPlaneModule } from '@/utils/planeModules'
import { getHookStatusLabel } from '@/utils/hookStatus'
import { getAssignableOrdersForPlane, getDeliveryOrderRisks } from '@/utils/operationalStatus'
import { calculateMissionProgress, getMissionDeliveryProgress } from '@/utils/routeMetrics'
import GroundStationStatusBadge from '@/components/ground-station/GroundStationStatusBadge'

export default {
  name: 'GroundStationMissionPanel',
  components: { GroundStationStatusBadge },
  props: {
    plane: { type: Object, default: null },
    embedded: { type: Boolean, default: false },
    compact: { type: Boolean, default: false },
    expanded: { type: Boolean, default: false }
  },
  data () {
    return {
      selectedOrderId: '',
      completionResult: '',
      unlockPending: false,
      taskBusy: false,
      routeWritePending: false,
      executionPending: false,
      finishingMission: false,
      manualResetPending: false,
      animateStep: false,
      unwatchQuestState: null,
      unwatchIsUnlock: null,
      unlockTimer: null,
      unlockWatchId: 0
    }
  },
  computed: {
    stepMotionState () { return [String(this.plane?.id || ''), this.compactStep, String(this.executeOrder?.id || '')] },
    missionStatePending () {
      return Boolean(this.executeOrder) && this.currentQuestState === 1
    },
    validMissionOrder () {
      const order = this.executeOrder
      return Boolean(order && order.main_status === '已付款' &&
        ['未申请', '拒绝退'].includes(order.refund_status) && order.mission_token)
    },
    missionOrderIdentity () {
      const order = this.executeOrder
      return order ? JSON.stringify([order.id, order.mission_token, order.main_status, order.refund_status]) : ''
    },
    standaloneReadyForOrder () {
      const state = this.plane?.planeState
      return Boolean(!this.executeOrder && this.plane?.mission_route_id && this.currentQuestState === 4 &&
        state?.online && state.espOnline && state.isUnlock === false)
    },
    missionActionBlocked () {
      return this.executeOrder ? !this.validMissionOrder : this.currentQuestState !== 1 && !this.standaloneReadyForOrder
    },
    manualResetAvailable () {
      const state = this.plane?.planeState
      return Boolean(!this.executeOrder && !this.plane?.mission_route_id && state?.online && state.espOnline &&
        state.isUnlock === false && state.orphanResetPhase === 'failed')
    },
    primaryVisible () {
      return !this.compact || this.currentQuestState !== 8
    },
    primaryLoading () {
      if (this.missionActionBlocked && !this.executeOrder) return this.manualResetPending
      return this.taskBusy || this.routeWritePending || this.currentQuestState === 2 || this.unlockPending || this.manualResetPending
    },
    primaryIcon () {
      if (this.missionActionBlocked && !this.executeOrder) return this.manualResetPending ? 'el-icon-loading' : 'el-icon-s-claim'
      if (this.taskBusy || this.routeWritePending || this.executionPending || this.currentQuestState === 2 || this.unlockPending || this.manualResetPending) return 'el-icon-loading'
      if (this.standaloneReadyForOrder) return 'el-icon-s-claim'
      if (this.currentQuestState === 4) return this.isPlaneUnlocked ? 'el-icon-video-play' : 'iconfont icon-jiesuo'
      return this.currentQuestState === 8 ? 'el-icon-loading' : 'el-icon-s-claim'
    },
    primaryLabel () {
      if (!this.plane) return '选择飞机'
      if (this.missionActionBlocked) {
        if (this.executeOrder) return '订单状态已变化'
        if (this.plane?.mission_route_id) return this.isPlaneUnlocked ? '请先加锁' : '航线执行中'
        return this.manualResetPending ? '复位中…' : (this.manualResetAvailable ? '飞机任务复位' : '飞机任务待复位')
      }
      if (this.routeWritePending) return '航点写入中…'
      if (this.missionStatePending) return this.taskBusy ? '准备中…' : '准备任务'
      if (this.compact && this.executeOrder && this.currentQuestState === 8) return '送达确认'
      if (this.currentQuestState === 1 || this.standaloneReadyForOrder) return this.taskBusy ? '准备中…' : '准备任务'
      if (this.currentQuestState === 2) return '航点写入中…'
      if (this.currentQuestState === 4) return !this.isPlaneUnlocked ? (this.unlockPending ? '解锁中…' : '重新解锁') : (this.executionPending ? '核对中…' : '执行任务')
      return this.isAutoGoodsDown ? '送餐下放中…' : '执行任务中…'
    },
    primaryDisabled () {
      if (this.missionActionBlocked) {
        return !this.manualResetAvailable || this.manualResetPending || this.finishingMission
      }
      return !this.plane || this.finishingMission || this.manualResetPending || this.taskBusy || this.routeWritePending || this.executionPending || this.currentQuestState === 2 || (this.currentQuestState === 8 && !(this.compact && this.executeOrder)) || (this.currentQuestState === 4 && this.unlockPending && !this.isPlaneUnlocked)
    },
    compactState () {
      if (!this.plane) return '请先选择飞机'
      if (!this.plane.planeState?.online) return '离线 · 无实时任务进度'
      if (this.completionResult && !this.executeOrder) return this.completionResult
      if (this.missionActionBlocked) return this.primaryLabel
      if (this.routeWritePending) return '航点写入中…'
      if (this.missionStatePending) return '准备任务'
      if (this.currentQuestState !== 8) return this.primaryLabel
      if (this.isAutoGoodsDown) return '送餐下放中'
      if (this.executeOrder) return `${this.executeOrder.receive_site_name || '配送站点'} · ${this.missionProgress === null ? '航线进度未知' : '配送中'}`
      return this.primaryLabel
    },
    compactInfo () {
      const order = this.executeOrder || this.selectedOrder
      if (!order) return this.compactState
      const site = order.receive_site_name || '未命名站点'
      const number = order.food_sn ? ` · 叫餐号 ${order.food_sn}` : ''
      const state = this.executeOrder ? this.compactState.replace(`${site} · `, '') : this.compactState
      return `${state} · ${site}${number}`
    },
    compactStep () {
      if (!this.plane?.planeState?.online) return -1
      if (this.routeWritePending) return 0
      if (this.missionStatePending) return -1
      if (this.completionResult && !this.executeOrder) return 3
      if (this.missionActionBlocked) return -1
      if (this.currentQuestState === 1) return this.taskBusy ? 0 : -1
      if (this.currentQuestState !== 8) return 0
      if (this.isAutoGoodsDown || (this.executeOrder && this.missionProgress !== null && this.missionDeliveryProgress !== null && this.missionProgress >= this.missionDeliveryProgress)) return 2
      return this.executeOrder || this.currentQuestState === 8 ? 1 : 0
    },
    availableOrders () {
      return getAssignableOrdersForPlane(this.$store.state.paidOrderList, this.plane)
    },
    availableOrderOptions () {
      return this.availableOrders.map(order => ({
        order,
        risks: getDeliveryOrderRisks(order, this.plane)
      }))
    },
    shippedOrders () {
      if (!this.plane) return []
      return (this.$store.state.paidOrderList || []).filter(order =>
        String(order.shop_id) === String(this.plane.shop_id) && order.shipment_status === '已发货'
      )
    },
    selectedOrder () {
      return this.availableOrders.find(order => String(order.id) === String(this.selectedOrderId)) || null
    },
    executeOrder () {
      if (!this.plane) return null
      return this.shippedOrders.find(order => String(order.by_plane_id) === String(this.plane.id)) || null
    },
    missionRisks () {
      return getDeliveryOrderRisks(this.executeOrder, this.plane, { executing: true })
    },
    selectedOrderRisks () {
      return getDeliveryOrderRisks(this.selectedOrder, this.plane)
    },
    routeList () { return this.$store.state.routeList || [] },
    currentQuestState () {
      const state = Number(this.plane?.planeState?.questState)
      return [1, 2, 4, 8].includes(state) ? state : 1
    },
    isPlaneUnlocked () {
      const value = this.plane?.planeState?.isUnlock
      return value === true || value === 1 || value === '1'
    },
    isAutoGoodsDown () {
      const value = this.plane?.planeState?.isAutoGoodsDown
      return value === true || value === 1 || value === '1'
    },
    showCargoConfirm () {
      return Boolean(this.executeOrder)
    },
    missionTitle () {
      if (!this.plane) return '等待选择飞机'
      return this.executeOrder ? `任务#${this.executeOrder.food_sn || '--'}` : '选择配送订单'
    },
    missionProgress () {
      const positions = this.plane?.planeState?.position
      const livePosition = Array.isArray(positions) && positions.length ? positions[positions.length - 1] : null
      return calculateMissionProgress(
        this.activeMissionRouteData,
        this.plane?.planeState?.missionCurrentSeq,
        livePosition
      )
    },
    activeMissionRouteData () {
      if (!this.executeOrder || !this.plane) return null
      const runtime = normalizeSiteRuntime(this.executeOrder)
      const slotIndex = runtime.runingArray.findIndex(id => String(id) === String(this.plane.id))
      if (slotIndex < 0) return null
      const routeId = runtime.bindRouteArray[slotIndex]
      const route = this.routeList.find(item => String(item.id) === String(routeId))
      if (!route || !route.route_data) return null
      try {
        return typeof route.route_data === 'string' ? JSON.parse(route.route_data) : route.route_data
      } catch (error) {
        return null
      }
    },
    missionDeliveryProgress () {
      return getMissionDeliveryProgress(this.activeMissionRouteData)
    },
    missionState () {
      if (!this.plane) return '--'
      return this.executeOrder ? '配送中' : '待命'
    }
  },
  watch: {
    stepMotionState (next, previous) {
      this.animateStep = next[0] === previous[0] && next[1] >= 0 && previous[1] >= 0 && next[1] !== previous[1]
    },
    executeOrder (order) { if (order) this.completionResult = '' },
    missionOrderIdentity () { this.clearUnlockWatchers() },
    availableOrders (orders) {
      if (!orders.some(order => String(order.id) === String(this.selectedOrderId))) this.selectedOrderId = ''
    },
    'plane.id' () {
      this.completionResult = ''
      this.selectedOrderId = ''
      this.clearUnlockWatchers()
    }
  },
  mounted () {
    document.addEventListener('mousedown', this.closeOrderSelectOnOutsidePointer, true)
    document.addEventListener('touchstart', this.closeOrderSelectOnOutsidePointer, true)
  },
  beforeDestroy () {
    document.removeEventListener('mousedown', this.closeOrderSelectOnOutsidePointer, true)
    document.removeEventListener('touchstart', this.closeOrderSelectOnOutsidePointer, true)
    this.clearUnlockWatchers()
  },
  methods: {
    runPrimary () {
      if (this.primaryDisabled) return
      if (this.compact && this.executeOrder && this.currentQuestState === 8) { this.$emit('open-details'); return }
      if (this.currentQuestState === 1 || this.standaloneReadyForOrder) {
        this.completionResult = ''
        if (this.compact && !this.selectedOrder && !this.executeOrder) { this.$emit('open-details'); this.$nextTick(() => this.$refs.orderSelect?.focus()); return }
        return this.checkQuest()
      }
      if (this.currentQuestState === 4) return this.isPlaneUnlocked ? this.executeMission() : this.watchUnlock()
    },
    async resetMissionManually () {
      if (this.manualResetPending || !this.manualResetAvailable) return
      const plane = this.plane
      this.manualResetPending = true
      plane.planeState.orphanResetPhase = 'trying'
      try {
        await resetOrphanMissionManually(plane, this.$store)
        plane.planeState.orphanResetPhase = 'confirmed'
        if (this.plane === plane) this.$message.success(t('飞机任务复位成功'))
      } catch (error) {
        plane.planeState.orphanResetPhase = 'failed'
        if (this.plane === plane) this.$message.error(t(error.message || '飞机任务复位失败'))
      } finally {
        this.manualResetPending = false
      }
    },
    closeOrderSelectOnOutsidePointer (event) {
      const select = this.$refs.orderSelect
      const control = select?.$refs?.control
      if (!control?.visible) return
      if (select.$el.contains(event.target) || control.$refs.popper?.$el?.contains(event.target)) return
      select.blur()
    },
    orderLabel (order) {
      return `#${order.food_sn || '--'} · ${order.receive_site_name || '未命名站点'}`
    },
    publish (payload) {
      if (!this.plane) return false
      return mqtt.publishCommand(this.plane, payload)
    },
    writeLog (content) {
      if (!this.plane || !content) return
      this.$store.dispatch('fetchLog', {
        sourceType: 'terminal',
        planeId: this.plane.id,
        planeName: this.plane.name,
        content
      })
    },
    publishWithLog (payload, content) {
      this.publish(payload)
      this.writeLog(content)
    },
    isCurrentMission (plane, orderId, token) {
      return !this._isDestroyed && !this._isBeingDestroyed && this.plane && plane &&
        String(this.plane.id) === String(plane.id) && String(this.plane.shop_id) === String(plane.shop_id) &&
        this.plane.plane_code === plane.plane_code && this.validMissionOrder && String(this.executeOrder.id) === String(orderId) &&
        this.executeOrder.mission_token === token
    },
    async executeMission () {
      const plane = this.plane
      const order = this.executeOrder
      if (!plane || !this.validMissionOrder) return
      const token = order.mission_token
      const hookStatus = plane.planeState?.hookstatus
      const locked = String(hookStatus || '').trim().toUpperCase() === 'HS_LOCKED' || hookStatus === '已锁定'
      if (hasPlaneModule(plane, 'cable_transport') && !locked) {
        try {
          await this.$confirm(t(`当前挂钩状态：${getHookStatusLabel(hookStatus)}。到卸货航点可能无法自动卸货，是否仍执行任务？`), t('挂钩未锁定'), { confirmButtonText: t('仍要执行'), cancelButtonText: t('取消'), type: 'warning' })
        } catch (error) { return }
      }
      if (!this.isCurrentMission(plane, order.id, token)) return
      if (this.executionPending) return
      this.executionPending = true
      try {
        await executeConfirmedMission(plane, order, () => this.isCurrentMission(plane, order.id, token))
        if (this.plane !== plane) return
        this.writeLog('飞机已接受本次送餐任务执行指令')
        speakText('执行送餐任务')
      } catch (error) {
        await Promise.allSettled(['fetchPaidOrderList', 'fetchSiteList'].map(action => this.$store.dispatch(action)))
        if (error.code === 'MISSION_TOKEN_MISMATCH' && this.isCurrentMission(plane, order.id, token)) {
          this.clearUnlockWatchers()
          this.$message.warning(t('飞机任务信息不一致，正在自动按未送达处理并复位'))
          await this.finishMission('undelivered', true)
          return
        }
        this.$message.error(t(error.message || '任务核对失败，未执行'))
      } finally {
        this.executionPending = false
      }
    },
    async checkQuest () {
      const order = this.executeOrder || this.selectedOrder
      if (!order) {
        this.$message.error(t('未选择订单任务！'))
        return
      }
      const risks = getDeliveryOrderRisks(order, this.plane, { executing: Boolean(this.executeOrder) })
      const blockingRisk = risks.find(risk => risk.blocking)
      if (blockingRisk) return this.$message.error(t(blockingRisk.message))
      if (this.executeOrder) {
        if (this.taskBusy) return
        this.taskBusy = true
        try { await this.continueQuest() } finally { this.taskBusy = false }
        return
      }

      for (const risk of risks.filter(item => item.requiresConfirmation)) {
        try {
          await this.$confirm(t(risk.confirmMessage), t('检测订单'), { confirmButtonText: t('继续'), cancelButtonText: t('放弃'), type: 'warning' })
        } catch (error) { return }
      }
      if (this.taskBusy) return
      this.taskBusy = true
      try { await this.runQuest() } catch (error) { this.$message.error(t(error.message || '任务准备失败')) } finally { this.taskBusy = false }
    },
    async runQuest () {
      const order = this.selectedOrder
      const plane = this.plane
      if (!order || !plane) return
      if (!mqtt.isConnected()) throw new Error('指令服务器未连接，未分配任务')
      const res = await startDeliveryTask({
        site_id: order.receive_site_id,
        shop_id: plane.shop_id,
        order_id: order.id,
        plane_id: plane.id
      })
      if (res.data.status !== 1) return
      if (this._isDestroyed || this._isBeingDestroyed || !this.plane ||
        String(this.plane.id) !== String(plane.id) || String(this.plane.shop_id) !== String(plane.shop_id) ||
        this.plane.plane_code !== plane.plane_code) throw new Error('任务已分配，但所选飞机已变化；请选回原飞机重新准备')

      // The backend task query below is authoritative even if a list refresh fails.
      await Promise.allSettled([this.$store.dispatch('fetchPaidOrderList')])
      const task = await getDeliveryTask({ shop_id: plane.shop_id, plane_id: plane.id, order_id: order.id })
      if (task.mission_token !== res.data.mission_token || task.plane_code !== plane.plane_code ||
        String(task.route_id) !== String(res.data.route_id)) throw new Error('任务已变化，请重新操作')
      if (this._isDestroyed || this._isBeingDestroyed || !this.plane ||
        String(this.plane.id) !== String(plane.id) || String(this.plane.shop_id) !== String(plane.shop_id) ||
        this.plane.plane_code !== plane.plane_code) throw new Error('任务已分配，但所选飞机已变化；请选回原飞机重新准备')
      if (!this.isCurrentMission(plane, order.id, task.mission_token)) {
        const list = this.$store.state.paidOrderList || []
        const allocated = {
          ...order,
          shipment_status: '已发货',
          by_plane_id: plane.id,
          mission_token: task.mission_token,
          mission_route_id: task.route_id
        }
        const otherOrders = list.filter(item => String(item.id) !== String(order.id) &&
          !(item.shipment_status === '已发货' && String(item.shop_id) === String(plane.shop_id) && String(item.by_plane_id) === String(plane.id)))
        this.$store.commit('setPaidOrderList', [...otherOrders, allocated])
      }
      if (!this.isCurrentMission(plane, order.id, task.mission_token)) throw new Error('任务已变化，请重新操作')
      let routeData
      try {
        routeData = this.prepareRoute(JSON.parse(task.route_data), order, task.mission_token)
      } catch (error) {
        this.$message.error(t('操作失败，后端返回的航线数据异常'))
        return
      }
      this.$emit('focus-route', JSON.parse(routeData))
      this.writeLog(`开始执行 取餐号：${order.food_sn || '--'}。`)
      mqtt.publishDelivery('start', this.plane, {
        plane_code: this.plane.plane_code,
        order_sn: order.order_sn,
        openid: order.openid
      })
      if (!this.publish(routeData)) throw new Error('航线发送失败，任务仍被占用，请重新准备')
      this.writeLog(`发送 取餐号：${order.food_sn || '--'} 的航线，等待飞机写入确认`)
      this.watchUnlock(res.data.mission_token)
    },
    async continueQuest () {
      const plane = this.plane
      const order = this.executeOrder
      try {
        const task = await getDeliveryTask({ shop_id: plane.shop_id, plane_id: plane.id, order_id: order.id })
        if (!this.isCurrentMission(plane, order.id, task.mission_token)) throw new Error('任务已变化，请重新操作')
        if (!plane.planeState?.online || !plane.planeState?.espOnline || plane.planeState.isUnlock !== false || this.currentQuestState !== 1) {
          throw new Error('飞机未在加锁待命状态，不能重新准备任务')
        }
        const routeData = this.prepareRoute(JSON.parse(task.route_data), order, task.mission_token)
        if (!this.publish(routeData)) throw new Error('航线发送失败，请检查指令服务器连接')
        this.writeLog(`重新发送 取餐号：${order.food_sn || '--'} 的航线`)
        this.watchUnlock(task.mission_token)
      } catch (error) {
        this.$message.error(t(error.message || '操作失败，航线异常'))
      }
    },
    prepareRoute (routeData, order, token) {
      if (!token) throw new Error('任务缺少确认标识')
      const route = JSON.parse(JSON.stringify(routeData))
      route.questAss.missionToken = token
      const values = { ...order, telTail: String(order.tel || '').slice(-4) }
      route.questAss.tasks.forEach(task => {
        const sound = typeof task.sound === 'string' ? task.sound.trim() : ''
        if (sound) task.sound = sound.replace(/\$(.*?)\$/g, (match, key) => values[key])
        else {
          delete task.sound
          delete task.sound_count
        }
      })
      return JSON.stringify(route)
    },
    watchUnlock (token) {
      this.clearUnlockWatchers()
      const plane = this.plane
      const order = this.executeOrder
      token = typeof token === 'string' ? token : order?.mission_token
      if (!order || !this.isCurrentMission(plane, order.id, token)) return
      this.routeWritePending = true
      const watchId = this.unlockWatchId
      const isCurrent = () => this.unlockWatchId === watchId && this.isCurrentMission(plane, order.id, token)
      const startUnlock = async () => {
        if (!isCurrent()) return
        this.routeWritePending = false
        this.unlockPending = true
        try {
          // Recheck the backend: an order refresh can lag behind cancellation.
          const task = await getDeliveryTask({ shop_id: plane.shop_id, plane_id: plane.id, order_id: order.id })
          if (!isCurrent()) return
          if (task.mission_token !== token || task.plane_code !== plane.plane_code) throw new Error('任务已变化，请重新操作')
          if (this.currentQuestState !== 4 || plane.planeState.missionToken !== token || plane.planeState.missionReady !== true || !plane.planeState.online || !plane.planeState.espOnline) {
            throw new Error('任务已变化，请重新操作')
          }
          if (this.isPlaneUnlocked) {
            this.writeLog('飞机已解锁，等待执行任务')
            this.clearUnlockWatchers()
            return
          }
          const status = await requestMissionReply(plane, 'missionStatusRequest', 'missionHandshake')
          if (!isCurrent() || status.missionToken !== token || !status.ready || Number(status.questState) !== 4) throw new Error('任务已变化，请重新操作')
          if (status.armed) { this.clearUnlockWatchers(); return }
          const reply = await requestMissionReply(plane, 'unlock', 'missionUnlockReply', { missionToken: token, handshakeId: status.requestId }, payload => {
            if (!isCurrent()) throw new Error('任务已变化，请重新操作')
            return dispatchDeliveryCommand({ shop_id: plane.shop_id, plane_id: plane.id, order_id: order.id, command: 'unlock', ...payload })
          })
          if (!reply.accepted) throw new Error(reply.reason || '解锁指令发送失败')
          this.writeLog('本次航点写入确认完成，发送解锁指令')
          this.unwatchIsUnlock = this.$watch('isPlaneUnlocked', unlocked => {
            if (!unlocked || !isCurrent()) return
            this.writeLog('飞机已解锁，等待执行任务')
            this.clearUnlockWatchers()
          })
          this.unlockTimer = setTimeout(() => {
            if (!isCurrent()) return
            this.$message.warning(t('解锁超时，请检查飞机状态'))
            this.writeLog('解锁超时，请检查飞机状态')
            this.clearUnlockWatchers()
          }, 15000)
        } catch (error) {
          await Promise.allSettled(['fetchPaidOrderList', 'fetchSiteList'].map(action => this.$store.dispatch(action)))
          if (!isCurrent()) return
          this.$message.error(t(error.message || '任务核对失败，未执行'))
          this.clearUnlockWatchers()
        }
      }
      this.unlockTimer = setTimeout(() => {
        if (!isCurrent()) return
        this.$message.error(t('航点写入确认超时，请检查飞机状态后重新准备任务'))
        this.clearUnlockWatchers()
      }, 55000)
      const readWriteState = () => [this.currentQuestState, this.plane?.planeState?.missionToken, this.plane?.planeState?.missionReady]
      const onWritten = state => {
        if (!isCurrent() || state[0] !== 4 || state[1] !== token || state[2] !== true || !plane.planeState.online || !plane.planeState.espOnline) return
        if (this.unwatchQuestState) this.unwatchQuestState()
        this.unwatchQuestState = null
        clearTimeout(this.unlockTimer)
        this.unlockTimer = null
        startUnlock()
      }
      this.unwatchQuestState = this.$watch(readWriteState, onWritten)
      onWritten(readWriteState())
    },
    clearUnlockWatchers () {
      this.unlockWatchId++
      if (this.unwatchQuestState) this.unwatchQuestState()
      if (this.unwatchIsUnlock) this.unwatchIsUnlock()
      if (this.unlockTimer) clearTimeout(this.unlockTimer)
      this.unwatchQuestState = null
      this.unwatchIsUnlock = null
      this.unlockTimer = null
      this.unlockPending = false
      this.routeWritePending = false
    },
    async finishMission (result, automatic = false) {
      if (!this.executeOrder || this.finishingMission) return
      const delivered = result === 'delivered'
      const plane = this.plane
      const order = this.executeOrder
      const token = order.mission_token
      this.finishingMission = true
      try {
        if (!automatic) {
          await this.$confirm(t(delivered ? '确认货物已送达？' : '确认货物未送达并使飞机复位？'), t('提示'), {
            confirmButtonText: t('确定'), cancelButtonText: t('取消'), type: 'warning'
          })
        }
        if (!this.isCurrentMission(plane, order.id, token)) return
        const res = await finishDeliveryTask({
          shop_id: this.plane.shop_id,
          order_id: order.id,
          plane_id: order.by_plane_id || this.plane.id,
          result,
          mission_token: token
        })
        if (res.data.status !== 1) {
          if (automatic) this.$message.error(t('自动未送达处理失败，任务未复位，请重试未送达'))
          return
        }
        // Order completion must not depend on aircraft availability or its reply.
        // The telemetry runtime resets the aircraft after it reports disarmed.
        if (this.plane !== plane || this._isDestroyed || this._isBeingDestroyed) return
        this.clearUnlockWatchers()
        plane.planeState.orphanResetPhase = 'waiting'
        this.writeLog(`配送状态处理 取餐号：${order.food_sn || '--'}`)
        this.completionResult = delivered ? '已送达 · 任务结束' : '未送达 · 任务结束'
        mqtt.publishDelivery('finish', this.plane, {
          plane_code: this.plane.plane_code,
          order_sn: order.order_sn,
          openid: order.openid
        })
      } catch (error) {
        if (error !== 'cancel' && error !== 'close') this.$message.error(t(error.message || '配送状态处理失败'))
      } finally {
        this.finishingMission = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.mission-panel { padding: 0 var(--ui-space-sm) var(--ui-space-md); }
.mission-panel.embedded { padding: 0; border-top: 0; }
.panel-heading { min-height: 44px; display: flex; align-items: center; justify-content: space-between; padding: 0 var(--ui-space-xs); }
.mission-card { padding: var(--ui-space-sm); }

.mission-summary-tile.ui-info-tile {
  width: 100%;
  min-height: 0;
  padding: 0;
  border: 0;
  border-radius: 0;
  background: transparent;
}
.mission-summary-tile ::v-deep .ui-info-tile__value { width: 100%; }
.mission-title { display: flex; justify-content: space-between; gap: var(--ui-space-sm); }
.mission-title strong { min-width: 0; overflow: hidden; font-size: var(--gs-font-root, 13px); text-overflow: ellipsis; white-space: nowrap; }
.mission-title > .ui-tag { flex: none; }
.compact-details .mission-summary-tile.ui-info-tile { box-sizing: border-box; height: 124px; overflow-y: auto; padding: 10px 12px; border: 1px solid var(--ui-border-color); border-radius: 10px; background: var(--ui-bg-elevated); }
.compact-details .mission-order-select-shell { margin-top: var(--ui-space-sm); }
.mission-footer { margin-top: var(--ui-space-sm); }
.mission-footer .mission-actions-label { margin-top: 0; }
.mission-timeline-reveal-enter-active,
.mission-timeline-reveal-leave-active {
  max-height: 120px;
  overflow: hidden;
  transition: max-height .24s var(--ui-ease-standard), opacity .18s ease, transform .24s var(--ui-ease-standard);
}
.mission-timeline-reveal-enter,
.mission-timeline-reveal-leave-to {
  max-height: 0;
  opacity: 0;
  transform: translateY(-8px);
}
dl { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, .7fr); gap: var(--ui-space-sm); margin: 0; }
dt { color: var(--ui-text-secondary); font-size: var(--gs-font-caption, 11px); }
dd { margin: var(--ui-space-xs) 0 0; overflow: hidden; font-size: var(--gs-font-body, 12px); text-overflow: ellipsis; white-space: nowrap; }
.mission-empty { color: var(--ui-text-secondary); font-size: var(--gs-font-micro, 10px); text-align: center; }
.mission-order-select-shell { position: relative; display: grid; gap: var(--ui-space-sm); margin-top: var(--ui-space-sm); padding-top: var(--ui-space-xs); }
.mission-order-step { margin: 0; color: var(--ui-text-secondary); font-size: var(--gs-font-caption, 11px); font-weight: 600; }
.mission-order-select { width: 100%; }
.mission-order-select ::v-deep .el-input__inner {
  padding: 0 38px 0 9px;
  font-size: var(--gs-font-body, 12px);
}
.mission-order-select-shell.has-selected-risks .mission-order-select ::v-deep .el-input__inner { color: var(--ui-color-danger); }
.mission-section-label { margin: 0; color: var(--ui-text-secondary); font-size: var(--gs-font-caption, 11px); font-weight: 600; }
.mission-selected-risks { display: flex; flex-wrap: wrap; align-items: center; gap: var(--ui-space-xs); padding: var(--ui-space-sm); border-left: 3px solid var(--ui-color-warning); background: var(--ui-bg-elevated); }
.mission-selected-risks ::v-deep .ui-tag { flex: none; width: 100%; min-width: 0; }
.mission-selected-risks ::v-deep .ui-tag,
.mission-warnings ::v-deep .ui-tag { height: auto; justify-content: flex-start; padding-top: var(--ui-space-xs); padding-bottom: var(--ui-space-xs); white-space: normal; }
.mission-selected-risks ::v-deep .ui-tag__text,
.mission-warnings ::v-deep .ui-tag__text { white-space: normal; overflow: visible; text-overflow: clip; overflow-wrap: anywhere; }
.mission-warning-label { margin-top: var(--ui-space-xs); }
.compact-details .mission-warning-label { margin-top: 14px; }
.compact-details .mission-selected-risks { margin-top: var(--ui-space-xs); }
.compact-details .mission-selected-risks ::v-deep .ui-tag__text,
.compact-details .mission-warnings ::v-deep .ui-tag__text { line-height: 1.5; }
.mission-warnings { display: grid; grid-template-columns: minmax(0, 1fr); gap: var(--ui-space-xs); margin-top: var(--ui-space-xs); padding: var(--ui-space-sm); border-left: 3px solid var(--ui-color-warning); background: var(--ui-bg-elevated); }
.mission-warnings ::v-deep .ui-tag { width: 100%; min-width: 0; }
.mission-actions-label { margin-top: var(--ui-space-sm); padding-top: 0; }
.mission-actions, .cargo-actions { display: grid; grid-template-columns: 1fr 1fr; gap: var(--ui-space-xs); margin-top: var(--ui-space-sm); }
.mission-actions .ui-button { grid-column: 1 / -1; }
</style>

<style lang="scss">
.ground-station-shop-menu.ground-station-mission-menu .el-select-dropdown__item {
  height: auto;
  min-height: 36px;
  padding: var(--ui-space-xs) 9px;
  line-height: 24px;
}

.mission-order-option {
  min-width: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--ui-space-xs);
}

.mission-order-option__alert {
  width: 14px;
  height: 14px;
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--ui-bg-page);
  border-radius: 50%;
  background: var(--ui-color-danger);
  font-size: 10px;
  font-weight: var(--ui-font-weight-bold);
  line-height: 1;
}

.mission-order-option__label {
  min-width: 0;
  flex: 1;
  overflow: hidden;
  color: var(--ui-text-primary);
  overflow-wrap: anywhere;
  white-space: normal;
}

.ground-station-mission-menu .el-select-dropdown__item.is-risky .mission-order-option__label {
  color: var(--ui-color-danger);
  font-weight: var(--ui-font-weight-semibold);
}

.ground-station-mission-menu .mission-order-option__risks .ui-tag {
  max-width: 100%;
  height: auto;
  white-space: normal;
}
.ground-station-mission-menu .mission-order-option__risks .ui-tag__text {
  white-space: normal;
  overflow-wrap: anywhere;
}

.mission-order-option__risks {
  min-width: 0;
  flex: 0 0 100%;
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--ui-space-xs);
}
</style>
